/* tinyyytales theme logic: volume bundles, free gift, cart upsells, bundles, sticky ATC.
   Works with Dawn 16's cart drawer, pubsub and product form. No external dependencies. */
(() => {
  'use strict';
  const TT = window.TT || {};
  const routes = window.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart' };
  const EVT = (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : { cartUpdate: 'cart-update', variantChange: 'variant-change' };
  const hasPubSub = typeof subscribe === 'function' && typeof publish === 'function';
  const on = (name, cb) => (hasPubSub ? subscribe(name, cb) : () => {});
  const emit = (name, data) => (hasPubSub ? publish(name, data) : Promise.resolve());

  /* ---------------- money ---------------- */
  const moneyFormat = String(TT.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
  function formatMoney(cents) {
    const n = Math.round(Number(cents) || 0);
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const m = moneyFormat.match(re);
    const key = m ? m[1] : 'amount';
    const fmt = (precision, thousands, decimal) => {
      const fixed = (n / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (frac ? decimal + frac : '');
    };
    let value;
    switch (key) {
      case 'amount_no_decimals': value = fmt(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(2, "'", '.'); break;
      default: value = fmt(2, ',', '.');
    }
    return m ? moneyFormat.replace(re, value) : '$' + value;
  }
  TT.formatMoney = formatMoney;

  const parseTiers = (str) => String(str || '')
    .split(',')
    .map((p) => p.split(':').map((x) => parseFloat(x)))
    .filter(([q, pct]) => q > 0 && pct >= 0)
    .map(([qty, pct]) => ({ qty, pct }))
    .sort((a, b) => a.qty - b.qty);
  const mixTiers = TT.mixMatch && TT.mixMatch.enabled ? parseTiers(TT.mixMatch.tiers) : [];
  const mixPctFor = (count) => mixTiers.reduce((best, t) => (count >= t.qty ? t.pct : best), 0);
  const nextMixTier = (count) => mixTiers.find((t) => count < t.qty);

  /* ---------------- cart API ---------------- */
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  const drawerEl = () => document.querySelector('cart-drawer');
  const sectionIdsForAdd = () => {
    const drawer = drawerEl();
    if (drawer && typeof drawer.getSectionsToRender === 'function') return drawer.getSectionsToRender().map((s) => s.id);
    return [];
  };

  const Cart = {
    get() {
      return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    },
    add(items, { withSections = true } = {}) {
      const body = { items };
      const ids = withSections ? sectionIdsForAdd() : [];
      if (ids.length) {
        body.sections = ids.join(',');
        body.sections_url = window.location.pathname;
      }
      return fetch(`${routes.cart_add_url}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || TT.strings.error);
          return data;
        });
    },
    change(key, quantity) {
      return fetch(`${routes.cart_change_url}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ id: key, quantity }) })
        .then((r) => r.json());
    },
    /* Re-render every cart surface on the page without opening the drawer. */
    async refresh() {
      const drawer = drawerEl();
      const ids = [];
      if (drawer) ids.push('cart-drawer');
      if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
      const itemsEl = document.getElementById('main-cart-items');
      const footerEl = document.getElementById('main-cart-footer');
      if (itemsEl && itemsEl.dataset.id) ids.push(itemsEl.dataset.id);
      if (footerEl && footerEl.dataset.id) ids.push(footerEl.dataset.id);
      if (!ids.length) return;
      const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } });
      const sections = await res.json();
      const parse = (html) => new DOMParser().parseFromString(html || '', 'text/html');
      if (drawer && sections['cart-drawer']) {
        const doc = parse(sections['cart-drawer']);
        const empty = !!doc.querySelector('cart-drawer.is-empty');
        const wasEmpty = drawer.classList.contains('is-empty') || !!drawer.querySelector('.drawer__inner-empty');
        if (empty !== wasEmpty) {
          // empty <-> filled: swap the whole drawer body, as Dawn's renderContents does
          const src = doc.querySelector('#CartDrawer');
          const target = drawer.querySelector('#CartDrawer');
          if (src && target) {
            target.innerHTML = src.innerHTML;
            const overlay = drawer.querySelector('#CartDrawer-Overlay');
            if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
          }
        } else {
          ['cart-drawer-items', '.cart-drawer__footer'].forEach((sel) => {
            const target = drawer.querySelector(sel);
            const source = doc.querySelector(sel);
            if (target && source) target.replaceWith(source);
          });
        }
        drawer.classList.toggle('is-empty', empty);
      }
      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        const src = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
        if (bubble && src) bubble.innerHTML = src.innerHTML;
      }
      if (itemsEl && sections[itemsEl.dataset.id]) {
        const cartItems = document.querySelector('cart-items');
        const src = parse(sections[itemsEl.dataset.id]).querySelector('cart-items');
        if (cartItems && src) {
          cartItems.innerHTML = src.innerHTML;
          cartItems.classList.toggle('is-empty', src.classList.contains('is-empty'));
        }
      }
      if (footerEl && sections[footerEl.dataset.id]) {
        const src = parse(sections[footerEl.dataset.id]).querySelector('.js-contents');
        const target = footerEl.querySelector('.js-contents');
        if (src && target) target.innerHTML = src.innerHTML;
      }
    },
    /* After an add: open the drawer with fresh sections, or go to the cart page. */
    async showAfterAdd(response) {
      const drawer = drawerEl();
      if (drawer && response && response.sections && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents(response);
      } else if (drawer) {
        await Cart.refresh();
        drawer.open && drawer.open();
      } else if (!document.getElementById('main-cart-items')) {
        window.location.href = routes.cart_url;
        return;
      } else {
        await Cart.refresh();
      }
    },
  };
  TT.cart = Cart;

  const setBusy = (btn, busy, label) => {
    if (!btn) return;
    if (busy) {
      btn.dataset.ttLabel = btn.dataset.ttLabel || btn.innerHTML;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (label) btn.textContent = label;
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
      if (btn.dataset.ttLabel) btn.innerHTML = btn.dataset.ttLabel;
      delete btn.dataset.ttLabel;
    }
  };

  async function addAndShow(items, btn, source) {
    if (btn && btn.getAttribute('aria-disabled') === 'true') return;
    setBusy(btn, true, TT.strings.adding);
    try {
      const response = await Cart.add(items);
      setBusy(btn, false);
      await Cart.showAfterAdd(response);
      emit(EVT.cartUpdate, { source: source || 'tt', cartData: response });
      return response;
    } catch (e) {
      setBusy(btn, false);
      window.alert(e.message || TT.strings.error);
      throw e;
    }
  }
  TT.addAndShow = addAndShow;

  const groupItems = (ids) => {
    const map = new Map();
    ids.forEach((id) => map.set(id, (map.get(id) || 0) + 1));
    return [...map.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
  };

  /* ---------------- free gift manager ---------------- */
  // Thresholds are configured in the store currency; convert when Shopify Markets shows another one.
  const currencyRate = () => {
    if (!TT.cartCurrency || TT.cartCurrency === TT.shopCurrency) return 1;
    const r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return r > 0 ? r : 1;
  };
  const Gift = {
    busy: false,
    declinedKey: 'tt_gift_declined',
    unpricedKey: 'tt_gift_unpriced',
    isGift: (item) => item.properties && item.properties._tt_gift,
    declined() {
      try { return sessionStorage.getItem(this.declinedKey) === '1'; } catch (e) { return false; }
    },
    unpriced() {
      try { return sessionStorage.getItem(this.unpricedKey) === '1'; } catch (e) { return false; }
    },
    // Safety net: the gift must be made free by an automatic discount in Shopify admin.
    // If the line still costs money, take it out so nobody is charged for a "free" gift.
    async dropIfCharged(cart) {
      let dropped = false;
      for (const line of cart.items.filter((i) => this.isGift(i))) {
        if (line.final_line_price > 0) {
          await Cart.change(line.key, 0);
          dropped = true;
        }
      }
      if (dropped) {
        try { sessionStorage.setItem(this.unpricedKey, '1'); } catch (e) {}
        console.warn('[tinyyytales] Free gift removed: no automatic discount made it free. Create the free-gift discount in Shopify admin (see SETUP-GUIDE.md).');
      }
      return dropped;
    },
    async sync() {
      const g = TT.gift || {};
      if (this.busy) return;
      this.busy = true;
      try {
        const cart = await Cart.get();
        const giftLines = cart.items.filter((i) => this.isGift(i));
        const eligible = cart.items.filter((i) => !this.isGift(i)).reduce((sum, i) => sum + i.final_line_price, 0);
        const variantId = parseInt(g.variantId, 10);
        const threshold = Math.round(g.threshold * currencyRate());
        const qualifies = g.enabled && variantId && eligible >= threshold && cart.item_count - giftLines.reduce((s, i) => s + i.quantity, 0) > 0;
        let changed = false;
        if (qualifies && !giftLines.length && !this.declined() && !this.unpriced()) {
          await Cart.add([{ id: variantId, quantity: 1, properties: { _tt_gift: TT.strings.giftLine } }], { withSections: false });
          changed = true;
          await this.dropIfCharged(await Cart.get());
        } else if (qualifies && giftLines.length && (await this.dropIfCharged(cart))) {
          changed = true;
        } else if (!qualifies && giftLines.length) {
          for (const line of giftLines) await Cart.change(line.key, 0);
          changed = true;
        } else if (qualifies && giftLines.length) {
          const [first, ...rest] = giftLines;
          if (first.quantity !== 1) { await Cart.change(first.key, 1); changed = true; }
          for (const line of rest) { await Cart.change(line.key, 0); changed = true; }
        }
        if (!qualifies) { try { sessionStorage.removeItem(this.declinedKey); } catch (e) {} }
        if (changed) {
          await Cart.refresh();
          emit(EVT.cartUpdate, { source: 'tt-gift' });
        }
      } catch (e) {
        console.warn('[tinyyytales] gift sync failed', e);
      } finally {
        this.busy = false;
      }
    },
    init() {
      if (!TT.gift || !TT.gift.enabled) return;
      on(EVT.cartUpdate, (event) => {
        if (event && event.source === 'tt-gift') return;
        // let Dawn finish its own re-render first
        setTimeout(() => this.sync(), 60);
      });
      document.addEventListener('click', (e) => {
        const remove = e.target.closest('cart-remove-button');
        if (remove && remove.closest('.tt-gift-line')) {
          try { sessionStorage.setItem(this.declinedKey, '1'); } catch (err) {}
        }
      }, true);
      this.sync();
    },
  };
  TT.gift_manager = Gift;

  /* ---------------- volume tiers ---------------- */
  class TTVolume extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.tiers = [...this.querySelectorAll('.tt-tier')];
      this.qtyInput = this.querySelector('[data-tt-qty]');
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.variantId = parseInt(this.dataset.variant, 10);
      this.unit = this.dataset.unit || 'item';
      this.units = this.dataset.units || 'items';
      const data = this.querySelector('[data-tt-variants]');
      this.variantData = data ? JSON.parse(data.textContent) : null;
      this.unitsBox = this.querySelector('[data-tt-units]');
      this.rowsEl = this.querySelector('[data-tt-unit-rows]');
      this.tiers.forEach((tier, i) => {
        tier.addEventListener('click', () => this.select(i));
        tier.addEventListener('keydown', (e) => this.onKey(e, i));
        tier.tabIndex = tier.getAttribute('aria-checked') === 'true' ? 0 : -1;
      });
      if (this.unitsBox) this.unitsBox.addEventListener('toggle', () => this.unitsBox.open && this.renderRows());
      this.unsub = on(EVT.variantChange, (event) => {
        const d = event && event.data;
        if (!d || d.sectionId !== this.dataset.section || !d.variant) return;
        this.price = d.variant.price;
        this.variantId = d.variant.id;
        this.updatePrices();
        if (this.unitsBox && this.unitsBox.open) this.renderRows(true);
        this.announce();
      });
      this.updatePrices();
      this.announce();
    }
    disconnectedCallback() { this.unsub && this.unsub(); }
    get index() { return Math.max(0, this.tiers.findIndex((t) => t.getAttribute('aria-checked') === 'true')); }
    get qty() { const t = this.tiers[this.index]; return t ? parseInt(t.dataset.qty, 10) : 1; }
    get discount() { const t = this.tiers[this.index]; return t ? parseFloat(t.dataset.discount) || 0 : 0; }
    totalFor(qty, pct) { return Math.round((this.price * qty * (100 - pct)) / 100); }
    get total() { return this.totalFor(this.qty, this.discount); }
    onKey(e, i) {
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      const next = (i + keys[e.key] + this.tiers.length) % this.tiers.length;
      this.select(next);
      this.tiers[next].focus();
    }
    select(i) {
      this.tiers.forEach((t, idx) => {
        t.setAttribute('aria-checked', idx === i ? 'true' : 'false');
        t.tabIndex = idx === i ? 0 : -1;
      });
      if (this.qtyInput) this.qtyInput.value = this.qty;
      if (this.unitsBox && this.unitsBox.open) this.renderRows();
      this.announce();
    }
    updatePrices() {
      this.tiers.forEach((t) => {
        const qty = parseInt(t.dataset.qty, 10);
        const pct = parseFloat(t.dataset.discount) || 0;
        const total = this.totalFor(qty, pct);
        const set = (sel, text) => { const el = t.querySelector(sel); if (el) el.textContent = text; };
        set('[data-total]', formatMoney(total));
        set('[data-compare]', formatMoney(this.price * qty));
        set('[data-each]', `${formatMoney(Math.round(total / qty))} / ${this.unit}`);
      });
    }
    label() {
      const q = this.qty;
      return `${q} ${q === 1 ? this.unit : this.units}`;
    }
    announce() {
      const btn = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      const span = btn && btn.querySelector('span');
      if (btn && span && !btn.disabled) span.textContent = `Add ${this.label()} · ${formatMoney(this.total)}`;
      document.dispatchEvent(new CustomEvent('tt:tier-change', { detail: { section: this.dataset.section, qty: this.qty, total: this.total, label: this.label() } }));
    }
    renderRows(force) {
      if (!this.rowsEl || !this.variantData) return;
      const qty = this.qty;
      const existing = [...this.rowsEl.querySelectorAll('.tt-units__row')];
      const current = this.variantData.variants.find((v) => v.id === this.variantId) || this.variantData.variants[0];
      if (force) existing.forEach((r) => r.remove());
      const rows = force ? [] : existing;
      for (let i = rows.length; i < qty; i++) {
        const row = document.createElement('div');
        row.className = 'tt-units__row';
        const label = document.createElement('span');
        label.textContent = `${this.unit.charAt(0).toUpperCase() + this.unit.slice(1)} ${i + 1}`;
        row.appendChild(label);
        this.variantData.options.forEach((name, oi) => {
          const values = [...new Set(this.variantData.variants.map((v) => v.options[oi]))];
          const sel = document.createElement('select');
          sel.setAttribute('aria-label', `${name} for ${this.unit} ${i + 1}`);
          sel.dataset.option = oi;
          values.forEach((val) => {
            const o = document.createElement('option');
            o.value = val; o.textContent = val;
            if (current && current.options[oi] === val) o.selected = true;
            sel.appendChild(o);
          });
          row.appendChild(sel);
        });
        this.rowsEl.appendChild(row);
      }
      [...this.rowsEl.querySelectorAll('.tt-units__row')].slice(qty).forEach((r) => r.remove());
    }
    rowVariantIds() {
      if (!this.rowsEl || !this.variantData) return [];
      return [...this.rowsEl.querySelectorAll('.tt-units__row')].map((row) => {
        const opts = [...row.querySelectorAll('select')].map((s) => s.value);
        const v = this.variantData.variants.find((vv) => vv.options.every((o, i) => o === opts[i]));
        return v ? v : null;
      });
    }
    isCustom() {
      if (!this.unitsBox || !this.unitsBox.open || this.qty < 2) return false;
      const ids = this.rowVariantIds();
      return ids.length === this.qty && ids.some((v) => !v || v.id !== this.variantId);
    }
    customItems() {
      const vs = this.rowVariantIds();
      const bad = vs.find((v) => !v || !v.available);
      if (bad !== undefined) throw new Error('One of the chosen combinations is unavailable. Please pick another.');
      return groupItems(vs.map((v) => v.id));
    }
  }
  if (!customElements.get('tt-volume')) customElements.define('tt-volume', TTVolume);

  /* Intercept the product form only when per-unit options differ. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const formId = form.getAttribute('id');
    const vol = formId ? document.querySelector(`tt-volume[data-form="${formId}"]`) : null;
    if (!vol || !vol.isCustom()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const btn = document.getElementById(`ProductSubmitButton-${vol.dataset.section}`);
    try {
      await addAndShow(vol.customItems(), btn, 'tt-volume');
    } catch (err) {}
    vol.announce();
  }, true);

  /* Cart upsells: buttons (not forms) because they live inside Dawn's cart forms. */
  document.addEventListener('change', (e) => {
    // keep Dawn's cart-items quantity handler from seeing our selects
    if (e.target.closest && e.target.closest('[data-tt-upsell]')) e.stopPropagation();
  }, true);
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-tt-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    const box = btn.closest('[data-tt-upsell]');
    const sel = box && box.querySelector('[data-tt-upsell-variant]');
    const id = Number(sel ? sel.value : btn.dataset.variantId);
    try { await addAndShow([{ id, quantity: 1 }], btn, 'tt-upsell'); } catch (err) {}
  });

  /* ---------------- PDP rewards progress ---------------- */
  function milestones() {
    const list = [];
    if (TT.gift && TT.gift.enabled && TT.gift.threshold > 0) list.push({ t: TT.gift.threshold, label: TT.gift.label });
    if (TT.orderDiscount && TT.orderDiscount.enabled && TT.orderDiscount.threshold > 0) list.push({ t: TT.orderDiscount.threshold, label: TT.orderDiscount.label });
    return list.sort((a, b) => a.t - b.t);
  }
  function fillFor(amount, ms) {
    if (!ms.length) return 100;
    const step = 100 / ms.length;
    let prevT = 0;
    for (let i = 0; i < ms.length; i++) {
      if (amount < ms[i].t) return Math.max(0, i * step + ((amount - prevT) / (ms[i].t - prevT)) * step);
      prevT = ms[i].t;
    }
    return 100;
  }
  class TTPdpProgress extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.ms = milestones();
      const box = this.querySelector('[data-tt-progress]');
      this.eligible = box ? parseInt(box.dataset.eligible, 10) || 0 : 0;
      this.extra = 0;
      document.addEventListener('tt:tier-change', (e) => {
        if (e.detail.section !== this.dataset.section) return;
        this.extra = e.detail.total;
        this.extraLabel = e.detail.label;
        this.render();
      });
      on(EVT.cartUpdate, () => setTimeout(() => this.reload(), 300));
      const vol = document.querySelector(`tt-volume[data-section="${this.dataset.section}"]`);
      if (vol && vol.total) { this.extra = vol.total; this.extraLabel = vol.label(); }
      this.render();
    }
    async reload() {
      try {
        const cart = await Cart.get();
        this.eligible = cart.items.filter((i) => !(i.properties && i.properties._tt_gift)).reduce((s, i) => s + i.final_line_price, 0);
        this.render();
      } catch (e) {}
    }
    render() {
      const box = this.querySelector('[data-tt-progress]');
      if (!box || !this.ms.length) return;
      const msg = box.querySelector('[data-tt-progress-msg]');
      const fill = box.querySelector('.tt-progress__fill');
      const steps = box.querySelectorAll('.tt-progress__step');
      const now = this.eligible;
      const projected = now + (this.extra || 0);
      const nextNow = this.ms.find((m) => now < m.t);
      const unlockedByThis = this.ms.filter((m) => now < m.t && projected >= m.t);
      if (!nextNow) {
        msg.innerHTML = 'Everything unlocked in your cart. Nice.';
      } else if (unlockedByThis.length) {
        msg.innerHTML = `Add ${this.extraLabel ? this.extraLabel : 'this'} and unlock <strong>${unlockedByThis.map((m) => m.label).join('</strong> + <strong>')}</strong>`;
      } else {
        const gap = nextNow.t - projected;
        msg.innerHTML = `With this, you're <strong>${formatMoney(gap)}</strong> away from <strong>${nextNow.label}</strong>`;
      }
      if (fill) fill.style.width = `${fillFor(projected, this.ms).toFixed(1)}%`;
      steps.forEach((s, i) => s.classList.toggle('is-done', !!this.ms[i] && projected >= this.ms[i].t));
    }
  }
  if (!customElements.get('tt-pdp-progress')) customElements.define('tt-pdp-progress', TTPdpProgress);

  /* ---------------- delivery estimate ---------------- */
  class TTDelivery extends HTMLElement {
    connectedCallback() {
      const d = TT.delivery || {};
      const addBusiness = (date, days) => {
        const r = new Date(date);
        let added = 0;
        while (added < days) { r.setDate(r.getDate() + 1); const wd = r.getDay(); if (wd !== 0 && wd !== 6) added++; }
        return r;
      };
      const addDays = (date, days) => { const r = new Date(date); r.setDate(r.getDate() + days); return r; };
      const today = new Date();
      const from = addDays(addBusiness(today, d.dispatchMin || 1), d.transitMin || 5);
      const to = addDays(addBusiness(today, d.dispatchMax || 3), d.transitMax || 15);
      const f = (x) => x.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      const wrap = this.querySelector('[data-tt-eta]');
      const range = this.querySelector('[data-tt-eta-range]');
      if (wrap && range) { range.textContent = `${f(from)} – ${f(to)}`; wrap.hidden = false; }
    }
  }
  if (!customElements.get('tt-delivery')) customElements.define('tt-delivery', TTDelivery);

  /* ---------------- sticky add to cart ---------------- */
  class TTStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (this.closest('quick-add-modal')) { this.remove(); return; }
      // escape transformed ancestors so position: fixed is relative to the viewport
      if (this.parentElement !== document.body) {
        const self = this;
        requestAnimationFrame(() => document.body.appendChild(self));
      }
      const target = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      this.meta = this.querySelector('[data-tt-sticky-meta]');
      if (target) {
        let ticking = false;
        const check = () => {
          ticking = false;
          const btn = document.getElementById(`ProductSubmitButton-${this.dataset.section}`) || target;
          const r = btn.getBoundingClientRect();
          const footer = document.querySelector('.footer, footer');
          const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
          this.classList.toggle('is-visible', r.bottom < 0 && !nearFooter);
        };
        const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        check();
      }
      document.addEventListener('tt:tier-change', (e) => {
        if (e.detail.section === this.dataset.section && this.meta) this.meta.textContent = `${e.detail.label} · ${formatMoney(e.detail.total)}`;
      });
      on(EVT.variantChange, (event) => {
        const d = event && event.data;
        if (!d || d.sectionId !== this.dataset.section) return;
        const btn = this.querySelector('[data-tt-sticky-btn]');
        if (btn) btn.disabled = !(d.variant && d.variant.available);
        if (d.variant && this.meta && !document.querySelector(`tt-volume[data-section="${this.dataset.section}"]`)) this.meta.textContent = formatMoney(d.variant.price);
      });
      const vol = document.querySelector(`tt-volume[data-section="${this.dataset.section}"]`);
      if (vol && vol.total && this.meta) this.meta.textContent = `${vol.label()} · ${formatMoney(vol.total)}`;
    }
  }
  if (!customElements.get('tt-sticky-atc')) customElements.define('tt-sticky-atc', TTStickyAtc);

  /* ---------------- bundle helpers (FBT, kits, builder) ---------------- */
  const priceOfSelect = (sel) => {
    const opt = sel && sel.selectedOptions && sel.selectedOptions[0];
    return opt ? parseInt(opt.dataset.price, 10) || 0 : 0;
  };
  const bundleSummary = (subtotal, count) => {
    const pct = mixPctFor(count);
    const total = Math.round((subtotal * (100 - pct)) / 100);
    return { pct, total, saved: subtotal - total };
  };

  class TTBundle extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.addEventListener('change', () => this.update());
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-tt-toggle]');
        if (toggle) { e.preventDefault(); this.toggleCard(toggle.closest('[data-tt-item]')); }
        const addAll = e.target.closest('[data-tt-add-all]');
        if (addAll) { e.preventDefault(); this.addAll(addAll); }
      });
      this.update();
    }
    items() { return [...this.querySelectorAll('[data-tt-item]')]; }
    picked() {
      return this.items().filter((el) => {
        if (el.hasAttribute('data-tt-fixed')) return true;
        const cb = el.querySelector('input[type="checkbox"]');
        if (cb) return cb.checked;
        return el.classList.contains('is-picked');
      });
    }
    toggleCard(card) {
      if (!card) return;
      card.classList.toggle('is-picked');
      const btn = card.querySelector('[data-tt-toggle]');
      if (btn) {
        const pickedNow = card.classList.contains('is-picked');
        btn.textContent = pickedNow ? 'Remove' : 'Add to bundle';
        btn.classList.toggle('button--secondary', pickedNow);
        btn.setAttribute('aria-pressed', pickedNow ? 'true' : 'false');
      }
      this.update();
    }
    update() {
      this.items().forEach((el) => {
        const cb = el.querySelector('input[type="checkbox"]');
        if (cb) el.classList.toggle('is-off', !cb.checked);
        const sel = el.querySelector('select[data-tt-variant]');
        const priceEl = el.querySelector('[data-tt-price]');
        if (sel && priceEl) priceEl.textContent = formatMoney(priceOfSelect(sel));
      });
      const picked = this.picked();
      const subtotal = picked.reduce((s, el) => s + priceOfSelect(el.querySelector('select[data-tt-variant]')), 0);
      const sum = bundleSummary(subtotal, picked.length);
      const set = (sel, text) => this.querySelectorAll(sel).forEach((n) => { n.textContent = text; });
      set('[data-tt-count]', String(picked.length));
      set('[data-tt-subtotal]', formatMoney(subtotal));
      set('[data-tt-total]', formatMoney(sum.total));
      this.querySelectorAll('[data-tt-compare]').forEach((n) => { n.textContent = sum.pct ? formatMoney(subtotal) : ''; n.hidden = !sum.pct; });
      this.querySelectorAll('[data-tt-saving]').forEach((n) => {
        n.textContent = sum.pct ? `You save ${formatMoney(sum.saved)} (${sum.pct}% bundle discount)` : '';
        n.hidden = !sum.pct;
      });
      const next = nextMixTier(picked.length);
      this.querySelectorAll('[data-tt-next]').forEach((n) => {
        if (!mixTiers.length) { n.textContent = ''; return; }
        n.innerHTML = next
          ? `Add <strong>${next.qty - picked.length}</strong> more to save <strong>${next.pct}%</strong>`
          : `Best bundle price unlocked: <strong>${mixPctFor(picked.length)}% off</strong>`;
      });
      const thumbs = this.querySelector('[data-tt-thumbs]');
      if (thumbs) {
        thumbs.innerHTML = '';
        picked.slice(0, 6).forEach((el) => {
          const img = el.querySelector('img');
          if (img) { const c = document.createElement('img'); c.src = img.currentSrc || img.src; c.alt = ''; c.width = 40; c.height = 40; thumbs.appendChild(c); }
        });
      }
      this.querySelectorAll('[data-tt-add-all]').forEach((b) => { b.disabled = picked.length === 0; });
    }
    async addAll(btn) {
      const picked = this.picked();
      if (!picked.length) return;
      const ids = picked.map((el) => Number(el.querySelector('select[data-tt-variant]').value));
      try { await addAndShow(groupItems(ids), btn, 'tt-bundle'); } catch (e) {}
    }
  }
  if (!customElements.get('tt-bundle')) customElements.define('tt-bundle', TTBundle);

  /* ---------------- boot ---------------- */
  const boot = () => Gift.init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
